import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SectionLabel } from './SectionLabel';

interface DialogHeaderProps {
  title: ReactNode;
  /** Petit libellé au-dessus du titre (« Questions / Tickets », « RGPD »…). */
  eyebrow?: ReactNode;
  /** Une ligne sous le titre (téléphone, précision). */
  subtitle?: ReactNode;
  icon?: LucideIcon;
  /** Teinte de la pastille d'icône. */
  tone?: 'brand' | 'warning';
  onClose: () => void;
  /** Flèche de retour à gauche du titre (modale à plusieurs écrans). */
  onBack?: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * L'en-tête de toutes les modales et tiroirs — un seul, pour qu'ils se
 * ressemblent : hauteur contenue, titre `text-strong`, icône dans une
 * pastille, et une croix qui est un **bouton** (fond, forme, taille
 * tactile), pas un glyphe qui flotte au milieu de nulle part.
 */
export function DialogHeader({
  title,
  eyebrow,
  subtitle,
  icon: Icon,
  tone = 'brand',
  onClose,
  onBack,
  disabled,
  className,
}: DialogHeaderProps) {
  return (
    <header
      className={cn(
        'flex items-center gap-3 px-4 py-3 border-b border-hair shrink-0 bg-surface',
        className,
      )}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          disabled={disabled}
          aria-label="Retour"
          className="shrink-0 w-8 h-8 -ml-1 rounded-full grid place-items-center text-ink-soft hover:bg-surface-alt disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      ) : Icon ? (
        <div
          className={cn(
            'shrink-0 w-8 h-8 rounded-lg grid place-items-center',
            tone === 'warning' ? 'bg-warning/15 text-warning' : 'bg-brand-soft text-brand',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <SectionLabel className={tone === 'warning' ? 'text-warning' : undefined}>
            {eyebrow}
          </SectionLabel>
        )}
        <h2 className="text-strong font-extrabold text-ink leading-tight truncate">{title}</h2>
        {subtitle && <div className="text-meta text-ink-mute mt-0.5 truncate">{subtitle}</div>}
      </div>
      <button
        type="button"
        onClick={onClose}
        disabled={disabled}
        aria-label="Fermer"
        className="shrink-0 w-8 h-8 -mr-1 rounded-full grid place-items-center bg-surface-alt text-ink-soft hover:bg-hair hover:text-ink transition-colors disabled:opacity-50"
      >
        <X className="h-4 w-4" />
      </button>
    </header>
  );
}
